import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";

const HINT_SMALL_SCREEN_QUERY = "(max-width: 1023px)";
const HINT_AUTO_HIDE_MS = 4500;

type ScrollFadeProps = {
  /** Classes for the scrollable container (flex, gap, overflow-x-auto, snap, etc.) */
  containerClassName?: string;
  /** Tailwind gradient "from" color that matches the surrounding background */
  fadeFrom?: string;
  /** Optional accessible label for the scroll region */
  ariaLabel?: string;
  /** Width of the fade overlay */
  fadeWidthClassName?: string;
  /** Short hint shown on small screens (e.g. "Desliza para ver más") */
  hint?: string;
  /** sessionStorage key: shows the hint once per app session (reappears next time the app opens) */
  hintSessionKey?: string;
  children: ReactNode;
};

/**
 * Adds soft gradient hints on the edges of a horizontal scroll area.
 * The hint only appears on the side where there is still content to see.
 * Optionally shows a one-time "swipe" tip on small screens.
 */
export default function ScrollFade({
  containerClassName = "",
  fadeFrom = "from-background-50",
  fadeWidthClassName = "w-10",
  ariaLabel,
  hint,
  hintSessionKey,
  children,
}: ScrollFadeProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(true);
  const [hintVisible, setHintVisible] = useState(false);
  const hintSeenRef = useRef(true);

  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setAtStart(el.scrollLeft <= 2);
    setAtEnd(el.scrollLeft >= max - 2);
  }, []);

  const dismissHint = useCallback(() => {
    setHintVisible(false);
    if (hintSessionKey && !hintSeenRef.current) {
      hintSeenRef.current = true;
      try {
        window.sessionStorage.setItem(hintSessionKey, "1");
      } catch {
        // almacenamiento no disponible: se mantiene solo en memoria
      }
    }
  }, [hintSessionKey]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    update();
    el.addEventListener("scroll", update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(el);
    window.addEventListener("resize", update);
    return () => {
      el.removeEventListener("scroll", update);
      observer.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [update]);

  // Swipe tip: only on small screens, once per app session.
  useEffect(() => {
    if (!hint || !hintSessionKey || typeof window === "undefined") return;
    let alreadySeen = false;
    try {
      alreadySeen = window.sessionStorage.getItem(hintSessionKey) === "1";
    } catch {
      alreadySeen = false;
    }
    if (alreadySeen) return;
    const mql = window.matchMedia(HINT_SMALL_SCREEN_QUERY);
    if (!mql.matches) return;
    hintSeenRef.current = false;
    setHintVisible(true);

    const timer = window.setTimeout(dismissHint, HINT_AUTO_HIDE_MS);
    const onChange = (event: MediaQueryListEvent) => {
      if (!event.matches) dismissHint();
    };
    mql.addEventListener("change", onChange);
    return () => {
      window.clearTimeout(timer);
      mql.removeEventListener("change", onChange);
    };
  }, [hint, hintSessionKey, dismissHint]);

  // Dismiss the tip as soon as the user actually scrolls the area.
  useEffect(() => {
    if (!hintVisible) return;
    const el = ref.current;
    if (!el) return;
    const onScroll = () => {
      if (el.scrollLeft > 2) dismissHint();
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [hintVisible, dismissHint]);

  const hasOverflow = !(atStart && atEnd);
  const showLeft = hasOverflow && !atStart;
  const showRight = hasOverflow && !atEnd;
  const showHint = Boolean(hint) && hintVisible && hasOverflow;
  const regionProps = ariaLabel ? { role: "region" as const, "aria-label": ariaLabel } : {};

  return (
    <div className="relative">
      <div ref={ref} className={containerClassName} {...regionProps}>
        {children}
      </div>
      <div
        aria-hidden="true"
        className={`pointer-events-none absolute inset-y-0 left-0 ${fadeWidthClassName} bg-gradient-to-r ${fadeFrom} to-transparent transition-opacity duration-200 ${
          showLeft ? "opacity-100" : "opacity-0"
        }`}
      />
      <div
        aria-hidden="true"
        className={`pointer-events-none absolute inset-y-0 right-0 ${fadeWidthClassName} bg-gradient-to-l ${fadeFrom} to-transparent transition-opacity duration-200 ${
          showRight ? "opacity-100" : "opacity-0"
        }`}
      />
      {hint ? (
        <div
          aria-hidden="true"
          className={`pointer-events-none absolute bottom-4 left-1/2 z-10 -translate-x-1/2 transition-opacity duration-300 lg:hidden ${
            showHint ? "opacity-100" : "opacity-0"
          }`}
        >
          <div className="flex items-center gap-2 rounded-full border border-background-300 bg-background-50/95 px-3.5 py-2 backdrop-blur-sm">
            <i className="ri-arrow-left-right-line text-primary-600 text-sm"></i>
            <span className="text-xs font-semibold text-foreground-800 whitespace-nowrap">{hint}</span>
            <i className="ri-arrow-right-line text-primary-600 text-sm vv-hint-nudge"></i>
          </div>
        </div>
      ) : null}
    </div>
  );
}